import { get, isBoolean, isEmpty, isFunction } from "lodash";
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { TokenManager, getIpAddress } from "@smfa/utils";

/**
 * Thunk that user ip!
 */
export const thunkUserIp = createAsyncThunk(
  "user/setIpAddress",
  async (_, { dispatch, getState }) => {
    // Check if we already have an IP address in state
    const currentState = getState();
    if (currentState.user.ip_address) {
      return currentState.user.ip_address;
    }

    const ipAddress = await getIpAddress(dispatch);
    return ipAddress; // Function already dispatches setIpAddress, just return the result
  },
);

export const createUserSlice = (config = {}) => {
  const { extraReducers = {}, initialState: customInitialState = {} } = config;

  const initialState = {
    data: { profile: {}, membership: {} },
    token: "",
    formToken: "",
    ip_address: "",
  };

  const userSlice = createSlice({
    name: "user",
    initialState,
    reducers: {
      holdUser: (state, action) => {
        state.data = action.payload;
      },
      loginUserSuccess: (state, action) => {
        // Make a safe copy of user data
        state.data = action.payload.user ? { ...action.payload.user } : {};

        // If there's a profile, safely create _displayName
        if (get(state, "user.profile")) {
          state.data.profile = { ...state.data.profile }; // Clone the profile to ensure it's extensible

          // Safely create display name only if first_name and last_name exist
          if (state.data.profile.first_name && state.data.profile.last_name) {
            const title = state.data.profile.title || "";
            const firstName = state.data.profile.first_name;
            const lastName = state.data.profile.last_name;

            state.data.profile._displayName = title
              ? `${title} ${firstName} ${lastName}`
              : `${firstName} ${lastName}`;
          } else {
            state.data.profile._displayName = "Member"; // Fallback
          }
        }

        state.data.isMembershipActive = !get(
          state,
          "data.membership.membership._is_expired",
          true,
        );

        // Store token - could be string or object
        const token = action.payload.token;

        // Extract token ID if it's an object for consistent state reference
        state.token =
          typeof token === "object" && token.id ? token.id.toString() : token;
        state.isAuthenticated = Boolean(state.token);

        // Store token in secure storage
        TokenManager.setToken(token);

        // Log state for debugging
        log.debug("userSlice:loginSuccess - Auth state updated:", {
          isAuthenticated: state.isAuthenticated,
          hasToken: !!state.token,
        });
      },
      logoutUser: (state) => {
        log.debug("userSlice:logout - Clearing auth state");
        state.data = {};
        state.token = "";
        state.isAuthenticated = false;
        // Token cookie is cleared by the caller (logoutUser / clearSession)
      },
      setFormToken: (state, action) => {
        state.formToken = action.payload;
      },
      setIpAddress: (state, action) => {
        state.ip_address = action.payload;
      },
      hasMembership: () => {},
    },
    extraReducers: (builder) => {
      // Add custom extra reducers
      if (isFunction(extraReducers)) {
        extraReducers(builder);
      }
    },
  });

  return userSlice;
};

const getAdminAssignedMembershipPlanType = (userData) => {
  const profilePlanType = userData?.profile?.plan_type;
  const profilePaymentStatus = userData?.profile?.payment_status;

  return profilePlanType && profilePaymentStatus === "complimentary"
    ? profilePlanType
    : null;
};

const getCurrentMembershipPlanType = (userData) => {
  const planType = userData?.membership?.membership?.plan_type;
  const isCurrent = get(userData, "membership.membership._is_expired") === false;

  if (planType && isCurrent) {
    return planType;
  }

  return getAdminAssignedMembershipPlanType(userData);
};

/**
 * Does the user have this non-expired membership?
 * This function also checks admin-assigned complimentary membership in user.data.profile.plan_type.
 * @param {object} userData - state.user.data
 * @param {array|boolean} [membershipTypes=true] - Array of membership types to check against, or true to check if any membership exists
 * @returns {boolean} - True if the user has the specified/any membership, false otherwise
 */
export const hasMembership = (userData, membershipTypes = true) => {
  const isAdminUser = userData?.role === "admin" || userData?.type === "admin";
  if (isAdminUser) {
    return true;
  }

  const currentPlanType = getCurrentMembershipPlanType(userData);
  const _membershipTypes =
    Array.isArray(membershipTypes) || isBoolean(membershipTypes)
      ? membershipTypes
      : [membershipTypes];
  if (isEmpty(membershipTypes) && membershipTypes !== true) {
    return false;
  }

  return _membershipTypes === true
    ? Boolean(currentPlanType)
    : _membershipTypes.includes(currentPlanType);
};

/**
 * Search for non-expired membership by string prefix.  For example "membership_"
 * This function also checks admin-assigned complimentary membership in user.data.profile.plan_type.
 * @param {object} userData - state.user.data.membership
 * @param {array|boolean} [prefixes=true] - Array of prefixes to check against, or true to check if any prefix exists
 * @returns {boolean} - True if the user has a membership with the specified prefix, false otherwise
 */
export const hasMembershipPrefix = (userData, prefixes = true) => {
  const isAdminUser = userData?.role === "admin" || userData?.type === "admin";
  if (isAdminUser) {
    return true;
  }

  const currentPlanType = getCurrentMembershipPlanType(userData);
  const _prefixes =
    Array.isArray(prefixes) || isBoolean(prefixes) ? prefixes : [prefixes];
  if (isEmpty(_prefixes) && _prefixes !== true) {
    return false;
  }

  //log.debug("hasMembershipPrefix()", { currentPlanType, _prefixes });
  return _prefixes === true
    ? Boolean(currentPlanType)
    : _prefixes.some((prefix) => currentPlanType?.startsWith(prefix));
};

export const userSlice = createUserSlice();

export const {
  holdUser,
  loginUserSuccess,
  logoutUser,
  setFormToken,
  setIpAddress,
} = userSlice.actions;
